import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalSearchEnum } from 'app/shared/enums/global-search.enum';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Subject, take, takeUntil } from 'rxjs';
import { GlobalSearchDatatableService } from '../global-search-datatable/global-search-datatable.service';
import { GlobalSearchItemModel, GlobalSearchModel, GlobalSearchResponseModel } from 'app/shared/models/global-search.model';
import { HelperService } from 'app/shared/services/helper.service';


@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    FormsModule,
    RouterModule,
  ],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss'
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @Input() globalSearch: string = '';
  @Input() gridUrl: string = '';
  @Input() isSidebar: boolean = false;
  @Input() searchData = {
    cases: [],
    problemRecords: [],
    knowledgeLibrary: [],
  } as GlobalSearchModel;

  isDataModalVisible: boolean = false;
  searching: boolean = false;

  @Output() showAllResultsEmitter = new EventEmitter<string>();

  searchInput$ = new Subject<string>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private router: Router,
    private globalSearchDatatableService: GlobalSearchDatatableService,
    private activeRoute: ActivatedRoute,
    private helperService: HelperService
  ) {
    this.searchInput$
    .pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(() => {
      this.search();
    });
  }

  ngOnInit(): void {}

  globalSearchUpdate(data: string) {
    this.globalSearch = data;
    if(this.globalSearch && this.globalSearch.length < 3) {
      return;
    }

    this.searchInput$.next(data);
  }

  search() {
    if(!!this.gridUrl) {
      this.simpleSearch();
    } else {
      this.multiSearch();
    }
  }

  multiSearch() {
    this.searching = true;
    
    forkJoin([
      this.globalSearchDatatableService.getElkSearch(GlobalSearchEnum.GRID_URL_CASES, this.globalSearch),
      this.globalSearchDatatableService.getElkSearch(GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS, this.globalSearch),
      this.globalSearchDatatableService.getElkSearch(GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY, this.globalSearch)
    ])
    .pipe(
      take(1),
      finalize(() => {
        this.searching = false;
        this.isDataModalVisible = true;
      })
    )
    .subscribe((
      res: [
        GlobalSearchResponseModel,
        GlobalSearchResponseModel,
        GlobalSearchResponseModel,
      ]
    ) => {
      
      this.searchData.cases = this.globalSearchDatatableService
        .mapSearchData(GlobalSearchEnum.GRID_URL_CASES, this.helperService.deepCopy(res[0].items));
      
      this.searchData.problemRecords = this.globalSearchDatatableService
        .mapSearchData(GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS, this.helperService.deepCopy(res[1].items));

      this.searchData.knowledgeLibrary = this.globalSearchDatatableService
        .mapSearchData(GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY, this.helperService.deepCopy(res[2].items));
    });
    
  }

  simpleSearch() {
    this.searching = true;

    this.globalSearchDatatableService.getElkSearch(this.gridUrl, this.globalSearch)
    .pipe(
      take(1),
      finalize(() => {
        this.searching = false;
        this.isDataModalVisible = true;
      })
    )
    .subscribe((res: GlobalSearchResponseModel) => {

      switch (this.gridUrl) {
        case GlobalSearchEnum.GRID_URL_CASES:
          this.searchData.cases = this.globalSearchDatatableService
            .mapSearchData(this.gridUrl, this.helperService.deepCopy(res.items));

          break;
        case GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS:
          this.searchData.problemRecords = this.globalSearchDatatableService
            .mapSearchData(this.gridUrl, this.helperService.deepCopy(res.items));

          break;
        case GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY: 
          this.searchData.knowledgeLibrary = this.globalSearchDatatableService
            .mapSearchData(this.gridUrl, this.helperService.deepCopy(res.items));
          break;
      }
    });

  }

  goToItem(itemKey: string | unknown, data: GlobalSearchItemModel) {
    let url = '';
    switch (itemKey) {
      case GlobalSearchEnum.ITEM_KEY_CASES:
        url ='case-form/';
        break;
      case GlobalSearchEnum.ITEM_KEY_PROBLEM_RECORDS:
        url = 'problem-record/';
        break;
      case GlobalSearchEnum.ITEM_KEY_KNOWLEDGE_LIBRARY:
        url = 'case-form/';
        break;  
    }
  
    this.router.navigate([url + data.id]);

    this.helperService.collapsedSidebarEmiter.emit(true);
    this.closePopup();

  }

  showAllResults(itemKey: string) {
    let gridUrl = '';

    switch (itemKey) {
      case GlobalSearchEnum.ITEM_KEY_CASES:
        gridUrl = GlobalSearchEnum.GRID_URL_CASES;
        break;
      case GlobalSearchEnum.ITEM_KEY_PROBLEM_RECORDS:
        gridUrl = GlobalSearchEnum.GRID_URL_PROBLEM_RECORDS;
        break;
      case GlobalSearchEnum.ITEM_KEY_KNOWLEDGE_LIBRARY:
        gridUrl = GlobalSearchEnum.GRID_URL_KNOWLEDGE_LIBRARY;
        break;
    }
  
    const currentPath = window.location.pathname;
    
    if(!!this.gridUrl) {
      this.showAllResultsEmitter.emit(this.globalSearch);
      this.router.navigate([], {
        relativeTo: this.activeRoute,
        queryParams: { search: this.globalSearch },
        queryParamsHandling: 'merge',
      });

    } 
    else if (this.isSidebar && (currentPath && currentPath.includes('global-search'))) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['global-search/', gridUrl], { queryParams: { search: this.globalSearch } });
      });
    }
    else {
      this.router.navigate(['global-search/', gridUrl], { queryParams: { search: this.globalSearch } });
    }

    this.helperService.collapsedSidebarEmiter.emit(true);
    this.closePopup();

  }

  closePopup() {
    this.isDataModalVisible = false;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
